import React from 'react';
import { Box, Typography } from '@mui/material';

export interface LookupListItemProps {
  /** Main text to display (primary content) */
  mainText: string;
  /** Secondary text to display (subtitle/caption) */
  secondaryText?: string;
  /** Custom styling for the container */
  sx?: React.ComponentProps<typeof Box>['sx'];
  /** Font size for main text */
  mainTextSize?: string;
  /** Custom styling for main text */
  mainTextSx?: React.ComponentProps<typeof Typography>['sx'];
  /** Custom styling for secondary text */
  secondaryTextSx?: React.ComponentProps<typeof Typography>['sx'];
}

/**
 * LookupListItem - Reusable Lookup List Item Component
 *
 * A standardized component for displaying main and secondary text content in lookup lists that provides:
 * - Consistent styling for lookup item display
 * - Flexible main and secondary text styling
 * - Customizable layout and appearance
 * - Responsive text sizing for lookup modals and lists
 *
 * @example
 * ```tsx
 * <LookupListItem
 *   mainText="ART001 - Product Description"
 *   secondaryText="Category: Electronics"
 *   mainTextSize="0.8rem"
 * />
 * ```
 */
export const LookupListItem: React.FC<LookupListItemProps> = ({
  mainText,
  secondaryText,
  sx,
  mainTextSize = '0.8rem',
  mainTextSx,
  secondaryTextSx,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        py: 0.5,
        ...sx
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          fontSize={mainTextSize}
          sx={{
            fontWeight: 500,
            ...mainTextSx
          }}
        >
          {mainText}
        </Typography>
        {secondaryText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={secondaryTextSx}
          >
            {secondaryText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default LookupListItem;